import { useMemo } from "react";
import { CartesianGrid, Line, LineChart, ReferenceArea, ResponsiveContainer, Scatter, Tooltip, XAxis, YAxis, Legend } from "recharts";
import { fmtUtc, POLLUTANT_COLOR, POLLUTANT_LABEL } from "@/lib/format";

// Builds a merged time axis; invalid/missing samples become null so gaps are drawn, not smoothed over.
export function SeriesChart({ seriesByKey, labels = {}, highlight, height = 260, showRaw = false, testId }) {
  const data = useMemo(() => {
    const map = new Map();
    Object.entries(seriesByKey).forEach(([key, rows]) => {
      rows.forEach((r) => {
        const t = r.observed_at;
        const row = map.get(t) || { t, ts: new Date(t).getTime(), flags: {} };
        const v = showRaw ? r.raw_value : r.value;
        row[key] = r.qc_flag === "INVALID" || r.qc_flag === "MISSING" || v == null ? null : v;
        if (r.qc_flag && r.qc_flag !== "VALID") row.flags[key] = r.qc_flag;
        if (r.qc_flag === "SUSPECT") row[`${key}__suspect`] = v;
        if (r.qc_flag === "INVALID") row[`${key}__invalid`] = 0;
        map.set(t, row);
      });
    });
    return [...map.values()].sort((a, b) => a.ts - b.ts);
  }, [seriesByKey, showRaw]);

  const keys = Object.keys(seriesByKey);
  if (!data.length) return <div className="py-8 text-center text-xs text-slate-500">No samples in window</div>;
  return (
    <div data-testid={testId} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, left: -10, bottom: 0 }}>
          <CartesianGrid stroke="#1E3E30" strokeDasharray="2 4" />
          <XAxis dataKey="ts" type="number" domain={["dataMin", "dataMax"]} tickFormatter={(v) => fmtUtc(new Date(v).toISOString())} stroke="#475569" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
          <YAxis stroke="#475569" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
          <Tooltip contentStyle={{ background: "#11241C", border: "1px solid #1E3E30", fontSize: 12 }} labelFormatter={(v) => fmtUtc(new Date(v).toISOString(), true)}
            formatter={(val, name, p) => {
              const base = name.replace(/__.*$/, "");
              const flag = p?.payload?.flags?.[base];
              return [`${val}${flag ? ` (${flag})` : ""}`, labels[base] || POLLUTANT_LABEL[base] || base];
            }} />
          {highlight && <ReferenceArea x1={new Date(highlight[0]).getTime()} x2={new Date(highlight[1]).getTime()} fill="#F59E0B" fillOpacity={0.12} stroke="#F59E0B" strokeOpacity={0.4} />}
          {keys.map((k, i) => (
            <Line key={k} type="monotone" dataKey={k} name={labels[k] || POLLUTANT_LABEL[k] || k} stroke={POLLUTANT_COLOR[k] || ["#90B849", "#fbbf24", "#a78bfa", "#f472b6"][i % 4]} dot={false} strokeWidth={1.6} connectNulls={false} isAnimationActive={false} />
          ))}
          {keys.map((k) => <Scatter key={`${k}s`} dataKey={`${k}__suspect`} name={`${labels[k] || k} suspect`} fill="#fbbf24" shape="diamond" legendType="none" isAnimationActive={false} />)}
          {keys.map((k) => <Scatter key={`${k}i`} dataKey={`${k}__invalid`} name={`${labels[k] || k} invalid`} fill="#ef4444" shape="cross" legendType="none" isAnimationActive={false} />)}
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function Sparkline({ points, color = "#90B849", height = 36, testId }) {
  const data = (points || []).map((p) => ({ v: p.qc_flag === "INVALID" ? null : p.value, t: p.observed_at }));
  if (!data.length) return <div className="text-[10px] text-slate-600">no data</div>;
  return (
    <div data-testid={testId} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
          <Line type="monotone" dataKey="v" stroke={color} dot={false} strokeWidth={1.3} isAnimationActive={false} connectNulls={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function WindRose({ samples, size = 180, testId }) {
  const bins = useMemo(() => {
    const b = Array(16).fill(0);
    (samples || []).forEach((s) => { b[Math.round(s.wind_from_deg / 22.5) % 16] += 1; });
    const max = Math.max(1, ...b);
    return b.map((n) => n / max);
  }, [samples]);
  const c = size / 2;
  const r = c - 14;
  return (
    <svg data-testid={testId} width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="mx-auto">
      {[0.33, 0.66, 1].map((k) => <circle key={k} cx={c} cy={c} r={r * k} fill="none" stroke="#1E3E30" />)}
      {bins.map((v, i) => {
        const a0 = ((i * 22.5 - 11.25 - 90) * Math.PI) / 180;
        const a1 = ((i * 22.5 + 11.25 - 90) * Math.PI) / 180;
        const rr = r * v;
        const d = `M${c},${c} L${c + rr * Math.cos(a0)},${c + rr * Math.sin(a0)} A${rr},${rr} 0 0 1 ${c + rr * Math.cos(a1)},${c + rr * Math.sin(a1)} Z`;
        return <path key={i} d={d} fill="#90B849" fillOpacity={0.35 + 0.5 * v} stroke="#90B849" strokeWidth={0.5} />;
      })}
      {["N", "E", "S", "W"].map((l, i) => {
        const a = ((i * 90 - 90) * Math.PI) / 180;
        return <text key={l} x={c + (r + 9) * Math.cos(a)} y={c + (r + 9) * Math.sin(a) + 3} fontSize="9" fill="#94a3b8" textAnchor="middle" fontFamily="JetBrains Mono">{l}</text>;
      })}
      <text x={c} y={size - 2} fontSize="8" fill="#64748b" textAnchor="middle" fontFamily="JetBrains Mono">wind FROM</text>
    </svg>
  );
}
